import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Button } from '@chakra-ui/react';

import { countries } from 'data';
import {
  RadioOptionsWithConfirm,
  YearInputWithConfirm,
  AutocompleteInputWithConfirm,
  IQuestion,
} from 'shared-components';

import { Q } from '../../../../../store/student/types';
import { IStudentNonUKVisaRefusal } from '../../../../../types';
import { validateYear } from './helpers';

interface IProps {
  index: number;
  country: string;
  isStudentVisa?: boolean;
  year: string;
  onSaveAndClose: (visa: IStudentNonUKVisaRefusal) => void;
}

const NonUKVisaRefusals: React.FC<IProps> = ({
  index,
  country: initialCountry,
  isStudentVisa: initialIsStudentVisa,
  year: initialYear,
  onSaveAndClose,
}) => {
  const countryNames: Array<string> = useMemo(() => countries.map(country => country.countryName), [countries]);

  const [countrySuggestions, setCountrySuggestions] = useState<Array<string>>(countryNames);
  const [country, setCountry] = useState<string>(initialCountry);
  const [isStudentVisa, setIsStudentVisa] = useState<boolean | undefined>(initialIsStudentVisa);
  const [year, setYear] = useState<string>(initialYear);
  const [complete, setComplete] = useState<boolean>(
    initialCountry !== '' && initialIsStudentVisa !== undefined && initialYear !== '',
  );
  const [yearErrorMsg, setYearErrorMsg] = useState<string>('');

  const handleNonUkVisaRefusalCountryChange = useCallback(
    (value: string) => {
      if (value) {
        const suggestions = countryNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));
        setCountrySuggestions(suggestions);
      } else {
        setCountrySuggestions(countryNames);
      }
      setCountry(value ?? '');
    },
    [countryNames],
  );

  const onBlurNonUkVisaRefusalCountry = useCallback(
    (value: string) => {
      if (!countrySuggestions.includes(value)) {
        setCountry('');
      }
      setCountrySuggestions(countryNames);
    },
    [countrySuggestions, countryNames],
  );

  const saveAndClose = useCallback(() => {
    onSaveAndClose({
      country,
      isStudentVisa,
      year,
    });
  }, [country, isStudentVisa, year, onSaveAndClose]);

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>): void => {
    saveAndClose();
  };

  useEffect(() => {
    setComplete(
      country !== '' && isStudentVisa !== undefined && year !== '' && year.length === 4 && yearErrorMsg === '',
    );
  }, [country, year, isStudentVisa, yearErrorMsg]);

  useEffect(() => {
    validateYear(setYearErrorMsg, year);
  }, [year]);

  return (
    <>
      <AutocompleteInputWithConfirm
        id={`non-uk-visa-refusal-country-${index}`}
        label={'What country issued the refusal?'}
        onChange={(evt: React.FormEvent<HTMLInputElement>) =>
          handleNonUkVisaRefusalCountryChange(evt.currentTarget.value)
        }
        value={country}
        suggestions={countrySuggestions}
        onBlur={(evt: React.FormEvent<HTMLInputElement>) => {
          onBlurNonUkVisaRefusalCountry(evt.currentTarget.value);
        }}
      />

      <RadioOptionsWithConfirm
        key={`${Q.PREV_NON_UK_VISA_REFUSAL_IS_STUDENT_VISA}-${index}`}
        id={`${Q.PREV_NON_UK_VISA_REFUSAL_IS_STUDENT_VISA}-${index}`}
        label={'Was the refusal for a student visa?'}
        onClick={(question: IQuestion) => setIsStudentVisa(question.label === 'Student Visa')}
        questions={[
          {
            char: '1',
            label: 'Student Visa',
            selected: isStudentVisa === true,
          },
          {
            char: '2',
            label: 'Other Visa',
            selected: isStudentVisa === false,
          },
        ]}
      />

      <YearInputWithConfirm
        key={`${Q.PREV_NON_UK_VISA_REFUSAL_YEAR}_${index}`}
        id={`${Q.PREV_NON_UK_VISA_REFUSAL_YEAR}_${index}`}
        value={year}
        label={'What year did you receive the refusal?'}
        onChange={(evt: React.FormEvent<HTMLInputElement>) => setYear(evt.currentTarget.value)}
        errorText={yearErrorMsg}
        placeholder="YYYY"
      />

      <Flex justifyContent="end">
        <Button
          id={`add-value-modal-confirm-button-${index}`}
          data-selector={`add-value-modal-confirm-button-${index}`}
          isDisabled={!complete}
          onClick={handleOnSaveAndCloseClick}
          size="lg"
          variant="solid"
        >
          Save &amp; Continue
        </Button>
      </Flex>
    </>
  );
};

export default NonUKVisaRefusals;
